import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, Glyphicon } from 'react-bootstrap';
import IntlComponent from 'axway-react-intl';
import { ContentMessage, ModalDialog } from 'axway-react-ui';
import * as utils from 'components/utility/utils';
import ModelInfoDialog from './ModelInfoDialog';
import JoinInfoDialog from './JoinInfoDialog';
import EditField from './EditField';
import ModelOps from './ModelOps';
import ModelInfo from './ModelInfo';
import JoinsList from './JoinsList';
import ModelFieldList from './ModelFieldList';
import CompositeFieldsSelect from './CompositeFieldsSelect';

/**
 * @param {object} editField is the edited field from the model
 * convertEditFieldToModelField() is a function which accepts like input a model field
 * change it and save it like a new field into the model.
 */
function convertEditFieldToModelField(editField) {
	const field = {
		type: utils.objectTypeToString(editField.fieldType),
		description: editField.fieldDescription,
		default: editField.defaultValue,
		required: editField.fieldRequired,
		readonly: editField.fieldReadOnly
	};
	if (editField.source) {
		field.model = editField.source;
	}
	return field;
}

class ModelDesignFields extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);

		this.cancelConfirmExecute = this.cancelConfirmExecute.bind(this);
		this.onEditModelInfo = this.onEditModelInfo.bind(this);
		this.updateModelInfo = this.updateModelInfo.bind(this);
		this.onDeleteJoinInfo = this.onDeleteJoinInfo.bind(this);
		this.onEditJoinInfo = this.onEditJoinInfo.bind(this);
		this.updateJoinInfo = this.updateJoinInfo.bind(this);
		this.onClose = this.onClose.bind(this);
		this.onDeleteField = this.onDeleteField.bind(this);
		this.onEditField = this.onEditField.bind(this);
		this.onFieldChanged = this.onFieldChanged.bind(this);
		this.onUpdateField = this.onUpdateField.bind(this);
		this.onCreateNewField = this.onCreateNewField.bind(this);
		this.onAddField = this.onAddField.bind(this);
		this.validateModel = this.validateModel.bind(this);
		this.onSelectFields = this.onSelectFields.bind(this);
		this.updateSelectedFields = this.updateSelectedFields.bind(this);
		this.commitSelectedFields = this.commitSelectedFields.bind(this);
		this.state = {
			modelDef: null,
			updatedField: null,
			isComposite: false,
			selectedFields: {},
			showFieldsModal: false
		};

		this.confirmation = {
			onJoinDelete: {
				label: this.intl.formatMsg('Delete'),
				bsStyle: 'danger',
				glyph: 'trash',
				summary: this.intl.formatMsg('Deleting the join will also remove all fields merged from that model.'),
				confirmation: this.intl.formatMsg('Are you sure?'),
				execute: (join) => this.confirmExecute(
					this.confirmation.onJoinDelete,
					() => this.onDeleteJoinInfo(join)
				)
			}
		};
	}

	/**
	 * Close the confirmation dialog.
	 */
	cancelConfirmExecute() {
		this.onClose();
	}

	/**
	 * Request a confirmation before excuting the function.
	 */
	confirmExecute(detail, fn) {
		this.setState({
			dialog: (
				<ContentMessage
					confirm
					className="lg warning confirm"
					summary={detail.summary}
					detail={detail.confirmation}
					actionLabel={detail.label}
					cancelLabel={this.intl.formatMsg('Cancel')}
					onAction={() => {
						this.cancelConfirmExecute();
						return fn();
					}}
					actionGlyph={detail.glyph}
					actionClass={detail.bsStyle && `btn-${detail.bsStyle}`}
					onClose={() => this.cancelConfirmExecute()}
					imgClass="gg icon-GG-warning"
				/>
			)
		});
	}

	/**
	 * @param {object} modelDef represent the model definition
	 * validateModel() accept as input the modelDef object and stands for
	 * validation of the object and for all of the fields in the model.
	 */
	validateModel(modelDef) {
		const valid = !!(modelDef && Object.keys(modelDef.fields).length);
		if (this.props.onValidate) {
			this.props.onValidate(valid);
		}
	}

	componentWillMount() {
		this.initializeModelDef(this.props);
	}

	componentWillReceiveProps(props) {
		this.initializeModelDef(props);
	}

	/**
	 * @param {object} props are the properties of the model.
	 */
	initializeModelDef(props) {
		/**
		 * Make a copy of the source model as we'll be tweaking the model itself
		 * Object.assign will not work.
		 */
		const modelDef = JSON.parse(JSON.stringify(props.modelDef));
		this.setState({
			modelDef,
			isComposite: modelDef.hasOwnProperty('parent')
		});
		this.validateModel(modelDef);
	}

	/**
	 * @param {object} modelDef is model definition object
	 * changed() is a function that accepts a modelDef object as an input
	 * serves for chanhging and validating the model.
	 */
	changed(modelDef) {
		// call the parent container
		this.props.onChange(modelDef);
		this.validateModel(modelDef);
	}

	/**
	 * @param {object} field is a field which going to be deleted.
	 * onDeleteField() is a function that accepts a field object as an input
	 * serves for deletion of field from the model's field list.
	 */
	onDeleteField(field) {
		const { modelDef } = this.state;
		ModelOps.deleteField(modelDef, field.name);
		this.changed(modelDef);
	}

	/**
	 * @param {object} item is the field object which going to be edited
	 * onEditField() is a function that accept a field i.e. 'item' as input object
	 * clone the field for edit. 'editFieldDialog will be used for input into the
	 * edit field dialog.  On change, the 'onFieldChanged' is called, temporarily
	 * storing the updated field. When the user clicks 'OK' on the dialog,
	 * 'onUpdateField' is called to make the actual change.
	 */
	onEditField(item) {
		const { modelDef } = this.state;
		const field = ModelOps.getField(modelDef, item.name);
		const existingFieldNames = ModelOps.getFieldNames(modelDef);

		/**
		 * If the model being edited has a parent, then the model is a composed model
		 * and it should not be possible to change the type.
		 */
		const disableType = this.state.isComposite;

		this.setState({
			updatedField: null,
			editFieldDialog: (
				<ModalDialog
					title={this.intl.formatMsg('Edit field')}
					okayText={this.intl.formatMsg('Update field')}
					closeText={this.intl.formatMsg('Cancel')}
					show
					onClose={() => { this.setState({ editFieldDialog: null }); }}
					onOkay={this.onUpdateField}
				>
					<EditField
						disableType={disableType}
						originalFieldName={item.name}
						fieldName={item.name}
						fieldType={utils.objectTypeToString(field.type)}
						fieldDescription={field.description}
						fieldReadOnly={field.readonly}
						fieldRequired={field.required}
						defaultValue={field.default}
						existingFieldNames={existingFieldNames}
						source={field.model}
						onChange={this.onFieldChanged}
						onSubmit={this.onUpdateField}
					/>
				</ModalDialog>
			)
		});
	}

	/**
	 * Start the modal selection of fields
	 * Pre-select all the fields in the current model we're composing from
	 * preSelectedFields == {
	 * 		modelA: [
	 * 			fieldNameA,
	 * 			fieldNameB
	 * 		]
	 * }
	 */

	onSelectFields() {
		const modelDef = JSON.parse(JSON.stringify(this.state.modelDef));
		const preSelectedFields = {};
		Object.keys(modelDef.fields).forEach(fieldName => {
			const collection = modelDef.fields[fieldName].model;
			if (!preSelectedFields[collection]) {
				preSelectedFields[collection] = [];
			}
			preSelectedFields[collection].push(fieldName);
		});
		this.setState({
			updatedField: null,
			editFieldDialog: null,
			showFieldsModal: true,
			selectedFields: preSelectedFields
		});
	}

	/**
	 * Update the selected fields object
	 * @param {object} update.parent is the modelName the update pertains to
	 * update.fields is an array of fieldNames in the model to id as selected
	 */
	updateSelectedFields(update) {
		if (!update.parent) {
			return;
		}
		const { selectedFields } = this.state;
		selectedFields[update.parent] = update.fields;
		// If model has no included fields then delete it
		Object.keys(selectedFields).forEach(modelName => {
			if (selectedFields[modelName].length === 0) {
				delete selectedFields[modelName];
			} else if (modelName !== update.parent) {
				update.fields.forEach((fieldName, i) => {
					// If we've seen a key before (like name) then we need to rename it to
					// name_{parentModel}
					if (selectedFields[modelName].indexOf(fieldName) >= 0) {
						selectedFields[update.parent][i] = `${fieldName}_${update.parent}`;
					}
				});
			}
		});
		this.setState({
			selectedFields
		});
	}

	/**
	 * commitSelectedFields() is a function which stands for saving/applying the model field list.
	 */
	commitSelectedFields() {
		// modelDef is the full definition of the composite model.
		const { modelDef } = this.state;
		// newly created object where will store the current state of the fields list.
		const newFields = {};
		// Update the model reference for each selected field to match that fields source.
		Object.keys(this.state.selectedFields).forEach(modelName => {
			this.state.selectedFields[modelName].forEach((fieldName) => {
				// `changedFieldName` will store the name of the changed field from the composite
				// model definition.
				const changedFieldName = Object.keys(modelDef.fields)
					.find(field => modelDef.fields[field].name === fieldName);
				/**
				 * Assign from modelDef if we can. Otherwise fallback on models.
				 * if the modelDef.fields exists and the fieldName is part of the modelDef fields,
				 * then add it as property of the newly object - 'newField'
				 */
				if (modelDef.fields && modelDef.fields[fieldName]) {
					newFields[fieldName] = modelDef.fields[fieldName];
					newFields[fieldName].model = modelName;
				/**
				 * if there is changed field in modelDef and its property name is equal to the
				 * fieldName of the parent model reference then add the changed field as property
				 * of the newly object i.e. 'newField'
				 */
				} else if (modelDef.fields
					&& changedFieldName
					&& modelDef.fields[changedFieldName].name
					&& modelDef.fields[changedFieldName].name === fieldName) {
					newFields[changedFieldName] = modelDef.fields[changedFieldName];
					newFields[changedFieldName].model = modelName;
				} else {
					newFields[fieldName] = this.props.models[modelName].fields[fieldName];
					newFields[fieldName].name = fieldName;
					newFields[fieldName].model = modelName;
				}
			});
		});
		this.setState({
			showFieldsModal: false,
			selectedFields: {}
		}, this.changed({
			...modelDef,
			fields: {
				...newFields
			}
		}));
	}

	/**
	 * @param {object} item is a field which will be edited.
	 * onFieldChanged() is a function that accepts a field object as an input
	 * stands for updating the state of the object.
	 */
	onFieldChanged(item) {
		this.setState({ updatedField: item });
	}

	/**
	 * onAddField() is a function which stands for adding already edited fields in the fields list
	 * and updating the state of the object.
	 */
	onAddField() {
		const { modelDef, updatedField } = this.state;
		const field = convertEditFieldToModelField(updatedField);
		ModelOps.addField(modelDef, updatedField.fieldName, field);
		this.setState({
			editFieldDialog: null,
			updatedField: null
		});
		this.changed(modelDef);
	}
	/**
	 * onUpdateField() is a function, once is called make the actual change
	 * before that, the 'onFieldChanged' is called, temporarily
	 * storing the updated field.
	 */
	onUpdateField() {
		const { modelDef, updatedField } = this.state;
		// if the field is never changed in the dialog, then updatedField can be null.
		if (updatedField) {
			const originalFieldName = updatedField.originalFieldName;
			const newField = convertEditFieldToModelField(updatedField);
			const originalField = ModelOps.getField(modelDef, originalFieldName);
			// get the name of the field that came from the linked model.
			const modelFieldName = originalField.name;
			// if there was a rename
			if (updatedField.fieldName !== originalFieldName) {
				// rename the field to the new NavigationMenuView
				ModelOps.renameField(modelDef, originalFieldName, updatedField.fieldName);
			}
			// if we found a modelFieldName, then set it
			if (modelFieldName) {
				newField.name = modelFieldName;
			}
			// update the field
			ModelOps.updateField(modelDef, updatedField.fieldName, newField);
		}
		this.setState({
			editFieldDialog: null,
			updatedField: null
		});
		if (updatedField) {
			// needs to be done after setState
			this.changed(modelDef);
		}
	}

	/**
	 * @param {object} data is the input object
	 * once the object is editted from onEditModelInfo()
	 * the updateModelInfo() function is called and make the actual change.
	 */
	updateModelInfo(data) {
		const { connectors } = this.props;
		const { modelDef } = this.state;

		modelDef.name = data.modelName;
		modelDef.connector = connectors.find(c => c.name === data.modelConnector);
		modelDef.description = data.modelDescription;
		this.setState({
			modelDef
		});
		this.changed(modelDef);
		this.onClose();
	}

	onClose() {
		this.setState({
			dialog: null,
			updatedField: null
		});
	}

	/**
	 * onEditModelInfo() is a function, once is called
	 * the model information is changed.
	 */
	onEditModelInfo() {
		this.setState({
			dialog: (
				<ModelInfoDialog
					mode={this.props.mode}
					models={this.props.models}
					values={{
						modelSource: this.state.modelDef
					}}
					connectors={this.props.connectors}
					onOkay={this.updateModelInfo}
					onClose={this.onClose}
					okayText={this.intl.formatMsg('Update')}
					closeText={this.intl.formatMsg('Cancel')}
				/>
			)
		});
	}

	/**
	 * @param {object} joinMeta is the metadata of the joined field.
	 * onDeleteJoinInfo() stands for deletion of some joined field
	 * from the model definition.
	 */
	onDeleteJoinInfo(joinMeta) {
		const { modelDef } = this.state;
		ModelOps.deleteJoinInfo(modelDef, joinMeta);
		this.changed(modelDef);
		this.setState({
			modelDef
		});
	}

	/**
	 * canAddJoin() is a function, that allows adding join into the composite model.
	 */
	canAddJoin() {
		const alreadyJoined = this.state.modelDef._joinmeta_.joinedModels || [];
		return Object.keys(this.props.models)
			.filter(
				m =>
					m !== this.state.modelDef.name
					&& m !== this.state.modelDef.parent
					&& alreadyJoined.indexOf(m) < 0
			).length > 0;
	}

	/**
	 * @param {*} joinMeta is matadata of the joined model.
	 * onEditJoinInfo() is a function, which accept metadata as an input
	 * stands for editing the data if the joined field/model.
	 */
	onEditJoinInfo(joinMeta) {
		const joinProps = {
			parentModel: this.state.modelDef.parent,
			parentField: '',
			joinType: '',
			joinModel: '',
			joinField: '',
			joinAs: '',
			joinAsFieldName: ''
		};

		if (joinMeta) {
			joinProps.parentModel = joinMeta.modelName;
			joinProps.joinType = joinMeta.type;
			joinProps.joinModel = joinMeta.model;
			joinProps.joinField = joinMeta.field;
			joinProps.parentField = joinMeta.fieldName;
			joinProps.joinAs = joinMeta.merge;
			joinProps.joinAsFieldName = (joinMeta.merge === 'fields')
				? undefined : joinMeta.relatedFields && joinMeta.relatedFields[0];
		}

		/**
		 * Only one join-type allowed per model, so limit
		 * if creating a second (or subsequent join) or editing
		 * a model with more than 1 join.
		 */
		const joins
			= this.state.modelDef._joinmeta_.joins || [];
		if ((!joinMeta && joins.length > 0) || (joinMeta && joins.length > 1)) {
			joinProps.availableJoinTypes = [ joins[0].type ];
		}

		// Filter out the models already joined - can only join once
		const alreadyJoined = this.state.modelDef._joinmeta_.joinedModels || [];
		const availableModels = Object.keys(this.props.models)
			.filter(
				// Filter out the already joined models and self.
				m => (
					(m === joinProps.joinModel || alreadyJoined.indexOf(m) < 0)
					&& m !== this.state.modelDef.name
				)
			)
			.reduce(
				(prev, m) => {
					prev[m] = this.props.models[m];
					return prev;
				}, {}
			);

		// Push the dialog into state to be displayed.
		this.setState({
			dialog: (
				<JoinInfoDialog
					mode={this.props.mode}
					models={availableModels}
					fields={Object.keys(this.state.modelDef.fields)}

					{...joinProps}

					onOkay={(data) => this.updateJoinInfo(data, joinMeta)}
					onClose={this.onClose}
					okayText={this.intl.formatMsg('Join')}
					closeText={this.intl.formatMsg('Cancel')}
				/>
			)
		});
	}

	updateJoinInfo(data, oldData) {
		const { modelDef } = this.state;
		if (oldData) {
			ModelOps.deleteJoinInfo(modelDef, oldData);
		}
		const joinModel = this.props.models[data.joinModel];
		ModelOps.addJoinInfo(modelDef, joinModel, data);
		this.changed(modelDef);
		this.setState({
			modelDef
		});
		this.onClose();
	}

	/**
	 * onCreateNewField() is a function, once is called create new field into the model.
	 */
	onCreateNewField() {
		const { modelDef } = this.state;
		const existingFieldNames = ModelOps.getFieldNames(modelDef);
		const disableType = false; // never possible to add new field for composite.

		this.setState({
			editFieldDialog: (
				<ModalDialog
					title={this.intl.formatMsg('Create field')}
					okayText={this.intl.formatMsg('Add field to model')}
					closeText={this.intl.formatMsg('Cancel')}
					show
					onClose={() => { this.setState({ editFieldDialog: null }); }}
					onOkay={this.onAddField}
				>
					<EditField
						disableType={disableType}
						existingFieldNames={existingFieldNames}
						onChange={this.onFieldChanged}
						onSubmit={this.onAddField}
					/>
				</ModalDialog>
			)
		});
	}

	render() {
		// const { mode } = this.props;
		const { editFieldDialog, dialog, showFieldsModal, modelDef } = this.state;
		const joinMeta = modelDef && modelDef._joinmeta_;
		// hard-coding for now, but when we implement join, this should be: mode === 'compose'
		const joinable = joinMeta && !!joinMeta.joinable; // && mode == 'compose' ?
		const joins = joinMeta && joinMeta.hasJoin ? joinMeta.joins : [];

		const fieldsSelector = (
			<ModalDialog
				title={this.intl.formatMsg('Select fields to include')}
				okayText={this.intl.formatMsg('Include selected fields')}
				closeText={this.intl.formatMsg('Cancel')}
				show
				onClose={() => {
					this.setState({ showFieldsModal: false, selectedFields: {} });
				}}
				onOkay={this.commitSelectedFields}
			>
				<CompositeFieldsSelect
					models={this.props.models}
					modelDef={modelDef}
					onChange={this.updateSelectedFields}
					selectedRows={this.state.selectedFields}
				/>
			</ModalDialog>
		);
		return (
			<div className="ux-container model-design-fields">
				{dialog}
				{editFieldDialog}
				{showFieldsModal ? fieldsSelector : null}
				<div>
					<ModelInfo
						modelName={this.state.modelDef.name}
						modelConnector={
							this.state.modelDef.connector && this.state.modelDef.connector.name
						}
						modelDescription={this.state.modelDef.description}
						onEdit={this.onEditModelInfo}
					>
						{!this.state.isComposite && (
							<Button
								bsSize="large"
								className="compact"
								onClick={this.onCreateNewField}
							>
								<Glyphicon glyph=" gg icon-GG-plus" />{this.intl.formatMsg('Field')}
							</Button>
						)}
						{
							[
								'compose',
								'edit'
							].indexOf(this.props.mode) >= 0
								&& modelDef.connector.name === 'composite' && (
								<Button
									bsSize="large"
									className="compact"
									onClick={this.onSelectFields}
								>
									<Glyphicon glyph=" gg icon-GG-cog" />{this.intl.formatMsg('Model fields')}
								</Button>
							)
						}
					</ModelInfo>
				</div>
				<ModelFieldList
					editable
					model={modelDef}
					models={this.props.models}
					onEdit={this.onEditField}
					onDelete={this.onDeleteField}
				/>
				{ joinable && (
					<JoinsList
						editable
						joins={joins}
						emptyMessage={this.intl.formatMsg('This model has no joins.')}
						onAdd={this.onEditJoinInfo}
						onAddEnabled={this.canAddJoin()}
						onEdit={this.onEditJoinInfo}
						onDelete={this.confirmation.onJoinDelete.execute}
					/>
				)}
			</div>
		);
	}
}

ModelDesignFields.propTypes = {
	modelDef: PropTypes.object.isRequired,
	models: PropTypes.object.isRequired,
	connectors: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired,
	mode: PropTypes.oneOf([
		'create', 'edit', 'compose'
	]).isRequired,
	onValidate: PropTypes.func
};

export default injectIntl(ModelDesignFields);
